"use client";

import { Session } from "next-auth";
import Link from "next/link";
import { useParams, usePathname, useRouter } from "next/navigation";
import {
    HTMLAttributeAnchorTarget,
    ReactNode,
    useEffect,
    useMemo,
    useState,
} from "react";
import { RiListSettingsLine, RiMenu3Line } from "react-icons/ri";
import { useHomePageRenderStore, useLeftSideBarStore } from "~/state/store";
import { api } from "~/utils/api";
import { cn, faktum } from "~/utils/fontsConfig";

import { NotificationMenu } from "../notifications-components/client-components/NotificationMenu";
import { LoginControlButton } from "../v3/themed/LoginControlButton";
import { If } from "./Conditional";
import { Button } from "./themed/Button";
import { RiverLogoSmall } from "./themed/Logo";
import { MobileNavSheet } from "./navigation/MobileNavSheet";
import { GlobalSearch } from "~/app/_components/GlobalSearch";
import { CreateEventButton } from "~/app/beta/home/_components/CreateEventModal";
import { getCommunitiesToHostQuery } from "~/app/_components/BottomNavBar";
import { signIn } from "next-auth/react";
import {
    CommunityIcon,
    DiscoverIcon,
    PeopleIcon,
    TicketIcon,
} from "~/app/_components/Icons";
import { Role } from "@prisma/client";

export const CustomNavLink = ({
    children,
    ...props
}: {
    children: ReactNode;
    href: string;
    target?: HTMLAttributeAnchorTarget | undefined;
    onClick?: () => void;
}) => {
    return (
        <Link className="relative" {...props} onClick={props.onClick}>
            <span
                className={`
            w-fit text-center font-faktum before:absolute
            before:bottom-0 before:left-0 before:h-[1px]
            before:w-full before:origin-right
            before:scale-x-0 before:bg-white
            before:transition-transform
            before:duration-500 hover:before:origin-left
            hover:before:scale-x-100 focus:rounded-xl focus:bg-black focus:bg-opacity-40
          `}
            >
                {children}
            </span>
        </Link>
    );
};

type CommunityManagementButtonProps = {
    sessionData: Session | null;
};

export const CommunityManagementButton = ({
    sessionData,
}: CommunityManagementButtonProps) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);

    return (
        <>
            {sessionData?.user.isManager && (
                <>
                    {sessionData?.user?.managedCommunities?.length === 1 ? (
                        <Link
                            href={`/${sessionData?.user?.managedCommunities[0]?.slug}`}
                        >
                            <Button
                                variant="secondaryLink"
                                className="underline"
                            >
                                Community Management
                            </Button>
                        </Link>
                    ) : null}

                    {sessionData?.user?.managedCommunities?.length > 1 && (
                        <div className="relative">
                            <Button
                                className="max-w-[70%] p-0 text-start underline md:max-w-full"
                                variant="secondaryLink"
                                onClick={() => setDropdownOpen(!dropdownOpen)}
                            >
                                Community Management
                            </Button>

                            {dropdownOpen && (
                                <div className="absolute w-48 rounded-md bg-v3-N-500 shadow-lg">
                                    {sessionData?.user?.managedCommunities?.map(
                                        (community) => (
                                            <Link
                                                key={community.slug}
                                                href={`/beta/${community.slug}`}
                                                className="block whitespace-normal break-words px-4 py-2 text-sm text-white hover:bg-v3-N-400"
                                                onClick={() =>
                                                    setDropdownOpen(false)
                                                }
                                            >
                                                {community.slug}
                                            </Link>
                                        ),
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </>
    );
};

const DesktopMenuV3 = ({
    sessionData,
    unreadNotificationsCount,
}: {
    sessionData: Session | null;
    unreadNotificationsCount: number;
}) => {
    const router = useRouter();
    const params = useParams();
    const communitySlug = params?.communitySlug;

    const { tab, setTab } = useHomePageRenderStore();
    const { data: communitiesToHost, isLoading } = getCommunitiesToHostQuery();

    const pathname = usePathname() ?? "";

    useEffect(() => {
        if (
            !pathname.includes("/beta/home") &&
            !pathname.includes("/discovery/communities")
        ) {
            setTab("none");
        }
    }, [pathname]);

    return (
        <div
            className={`
            hidden w-full min-w-fit items-center justify-between text-[16px] 
            text-white lg:flex lg:gap-6 
            ${!sessionData?.user && "w-max"}
            `}
        >
            <If condition={!!sessionData?.user}>
                <div className="flex flex-row items-center gap-2 px-4">
                    <Button
                        variant={tab === "events" ? "selectedGhost" : "ghost"}
                        className="flex h-10 flex-row items-center gap-2"
                        onClick={() => {
                            setTab("events");
                            router.push("/beta/home");
                        }}
                    >
                        <TicketIcon isSelected={tab === "events"} />
                        Events
                    </Button>
                    <Button
                        variant={
                            tab === "communities" ? "selectedGhost" : "ghost"
                        }
                        className="flex h-10 flex-row items-center gap-2"
                        onClick={() => {
                            setTab("communities");
                            router.push("/beta/home");
                        }}
                    >
                        <CommunityIcon isSelected={tab === "communities"} />
                        Communities
                    </Button>
                    <Button
                        variant={tab === "people" ? "selectedGhost" : "ghost"}
                        className="flex h-10 flex-row items-center gap-2"
                        onClick={() => {
                            setTab("people");
                            router.push("/beta/home");
                        }}
                    >
                        <PeopleIcon isSelected={tab === "people"} />
                        People
                    </Button>

                    <Button
                        variant={tab === "discover" ? "selectedGhost" : "ghost"}
                        className="flex h-10 flex-row items-center gap-2"
                        onClick={() => {
                            router.push("/discovery/communities");
                            setTab("discover");
                        }}
                    >
                        <DiscoverIcon isSelected={tab === "discover"} />
                        Discover
                    </Button>
                </div>
            </If>

            <div className="flex flex-row items-center gap-4">
                {sessionData?.user.role === "ADMIN" && (
                    <>
                        <CustomNavLink href="/admin" target="_blank">
                            Admin
                        </CustomNavLink>
                    </>
                )}

                <div className="flex flex-row items-center gap-4">
                    <If condition={!!sessionData?.user}>
                        {/* <CommunityManagementButton sessionData={sessionData} /> */}
                        {sessionData?.user && !isLoading && (
                            <CreateEventButton
                                user={sessionData.user}
                                communities={communitiesToHost}
                                communitySlug={communitySlug as string}
                                type="link"
                            />
                        )}
                        <div className="hidden flex-col items-center md:flex">
                            <GlobalSearch forceCompact={true} />
                        </div>
                        <NotificationMenu
                            count={unreadNotificationsCount}
                            isUrlTriggable={true}
                        />
                        <div />
                    </If>

                    <LoginControlButton session={sessionData} />
                </div>
            </div>
        </div>
    );
};

const NavMobileV3 = ({
    sessionData,
    navbarState,
    hasPermissions,
    unreadNotificationsCount,
}: {
    sessionData: Session | null;
    navbarState: NavbarState;
    hasPermissions: boolean | undefined;
    unreadNotificationsCount: number;
}) => {
    const params = useParams() ?? {};

    const { setSideBarState } = useLeftSideBarStore();

    const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);

    const handleMobileMenuToggle = () => {
        setMobileMenuOpen(!isMobileMenuOpen);
    };
    const closeMobileMenu = () => {
        setMobileMenuOpen(false);
    };

    const renderSubSidebarTrigger = () => {
        // if user is logged out show nothing
        if (!sessionData?.user) return null;
        if (!hasPermissions) return null;

        if (navbarState !== "") {
            return (
                <RiListSettingsLine
                    className="cursor-pointer text-4xl text-white"
                    onClick={() => {
                        setSideBarState(navbarState);
                    }}
                />
            );
        }
    };

    return (
        <div className="relative flex w-full lg:hidden">
            <div className="flex w-full items-center justify-between gap-4">
                <div>{renderSubSidebarTrigger()}</div>

                <div className="flex flex-row items-center gap-4">
                    <Link
                        href="/discovery/communities"
                        className="block md:hidden"
                    >
                        <DiscoverIcon isSelected={false} />
                    </Link>
                    <If condition={!!sessionData?.user}>
                        <RiMenu3Line
                            onClick={handleMobileMenuToggle}
                            className="min-w-14 cursor-pointer text-white"
                            size={32}
                        />
                    </If>
                    <If condition={!sessionData?.user}>
                        <div className="flex gap-3">
                            <Button
                                onClick={() => {
                                    signIn().catch(console.debug);
                                }}
                                variant="link"
                            >
                                Log in
                            </Button>
                            <Button
                                onClick={() => {
                                    signIn().catch(console.debug);
                                }}
                            >
                                Sign Up
                            </Button>
                        </div>
                    </If>
                </div>
            </div>

            <MobileNavSheet
                isOpen={isMobileMenuOpen}
                closeMobileSheet={closeMobileMenu}
                sessionData={sessionData}
            ></MobileNavSheet>
        </div>
    );
};

type NavbarState =
    | "event-management"
    | "community-management"
    | "admin-pannel"
    | "";

const Navbar = ({
    sessionData,
    isInBeta,
}: {
    sessionData: Session | null;
    isInBeta: boolean;
}) => {
    const router = useRouter();
    const params = useParams() ?? {};
    const pathname = usePathname() ?? "";
    const { setTab } = useHomePageRenderStore();
    // The route can't change during the lifetime of the component, we can memoize it
    const navbarState = useMemo(() => {
        const keys = Object.keys(params);
        if (keys.includes("slug")) {
            return "event-management";
        } else if (keys.includes("communitySlug")) {
            return "community-management";
        } else if (pathname.includes("/admin")) {
            return "admin-pannel";
        }
        return "";
    }, [params, pathname]);

    const { data: permissions } = api.permissions.getUserPermissions.useQuery(
        {
            event: params.slug
                ? {
                      slug: params.slug as string,
                  }
                : undefined,
            community: params.slug
                ? {
                      fromEventSlug: params.slug as string,
                  }
                : params.communitySlug
                  ? {
                        slug: params.communitySlug as string,
                    }
                  : undefined,
            adminCheck: true,
        },
        {
            enabled: !!params.slug && navbarState === "event-management",
        },
    );

    //  hack to make sure anonymous users are not interprated as logged in users, (show login btns)
    const session =
        sessionData?.user.role !== Role.ANONYMOUS ? sessionData : null;

    const { data: unreadNotificationsCount } =
        api.notification.getUnreadNotificationsCount.useQuery(undefined, {
            enabled: !!session?.user,
        });

    const hasPermissions =
        permissions?.isAdmin ||
        permissions?.isHost ||
        permissions?.isManager ||
        permissions?.isPreapprovedHost;

    return (
        <div className="fixed z-[100] flex h-max w-full justify-center bg-v3-N550">
            <div className="absolute z-[99] h-[80px] w-full bg-v3-N550 bg-opacity-60 backdrop-blur-md" />
            <div className="absolute h-[81px] w-full border-b-2 border-v3-N-500" />
            <nav className="fixed z-[101] flex h-max min-h-[85px] w-full justify-between px-4 sm:max-w-[95vw] md:min-h-[76px] md:max-w-desktop md:p-5">
                <div className="flex flex-row items-center gap-3 lg:space-x-10">
                    <div
                        className={cn(
                            "hidden items-center  gap-4 lg:flex",
                            (!hasPermissions || navbarState === "") && "flex",
                        )}
                    >
                        <button
                            onClick={() => {
                                setTab("events");
                                router.push("/beta/home");
                            }}
                        >
                            <div className="flex h-6 w-6 items-center justify-center overflow-hidden">
                                <RiverLogoSmall />
                            </div>
                        </button>
                        {/* {isInBeta && (
                            <Button
                                variant="tertiary"
                                size="xs"
                                className="text-xs opacity-50 hover:opacity-100"
                                onClick={async () => {
                                    await deleteBetaExperienceCookie();

                                    const currentPath =
                                        window.location.pathname;
                                    if (currentPath.includes("/beta/")) {
                                        const newPath = currentPath.replace(
                                            "/beta/",
                                            "/",
                                        );
                                        router.push(newPath);
                                        return;
                                    }
                                    router.push("/home");
                                }}
                            >
                                Leave Beta
                            </Button>
                        )} */}
                    </div>

                    <div className="flex flex-col items-center md:hidden">
                        <GlobalSearch />
                    </div>
                </div>

                <NavMobileV3
                    sessionData={session}
                    navbarState={navbarState}
                    hasPermissions={hasPermissions}
                    unreadNotificationsCount={unreadNotificationsCount ?? 0}
                />
                <DesktopMenuV3
                    sessionData={session}
                    unreadNotificationsCount={unreadNotificationsCount ?? 0}
                />
            </nav>
        </div>
    );
};

export default Navbar;
